import { SWRConfig } from "swr";

import { CONFIG } from "config";
import { logException } from "utils";

import { KeystoneApiClient } from "./keystone-api-client";
import { SSError } from "./ss-error";

// Parse data from server
let ssData: SSData = {
  isAdmin: false,
  isImpersonated: false,
  deviceId: "",
  featureFlags: {},
  featureFlagsOverrides: {},
};
try {
  const parsed = JSON.parse(window.SS_DATA);
  if (isSSData(parsed)) {
    ssData = parsed;
  } else {
    throw new SSError("invalid SS_DATA, couldn't hydrate on page load");
  }
} catch (err) {
  logException(err);
}

// Only use for debugging! Type SS_STATE in the console to view.
window.SS_STATE = ssData;

/**
 * Device ID from HTTP-only cookie.
 */
export const DEVICE_ID = ssData.deviceId;

/**
 * Selected policy ID.
 */
export const POLICY_ID = ssData.policyId;

/**
 * Feature flags from server are set for the life of the page.
 */
export const FEATURE_FLAGS = ssData.featureFlags;

/**
 * Basic user data provided by server.
 */
export const USER = ssData.user;

/**
 * If true, this is an admin user.
 */
export const IS_ADMIN = ssData.isAdmin;

/**
 * If true, the current user is being impersonated.
 */
export const IS_IMPERSONATED = ssData.isImpersonated;

/**
 * Reference to centralized instance of the Keystone API client.
 *
 * It's also available via the useKeystone hook with the React context.
 */
export const KEYSTONE = new KeystoneApiClient(CONFIG.KEYSTONE_API_HREF);
if (USER?.token) {
  KEYSTONE.setToken(USER.token);
}

// Hydrate SWR cache with data from server
const fallback: { [key: string]: unknown } = {};
if (USER && ssData.policies) {
  fallback.policies = ssData.policies;
  fallback.uievents = ssData.uiEvents;
}

/**
 * Thin wrapper around global SWR config with hydrated server-side data.
 */
export const SSDataStoreProvider = ({ children }) => {
  return <SWRConfig value={{ fallback }}>{children}</SWRConfig>;
};

// Validate parsed data type
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isSSData(data: any): data is SSData {
  if (!data || typeof data !== "object") return false;

  // Required fields
  if (typeof data.deviceId !== "string") {
    return false;
  }
  if (!data.featureFlags || typeof data.featureFlags !== "object") {
    return false;
  }
  if (
    !data.featureFlagsOverrides ||
    typeof data.featureFlagsOverrides !== "object"
  ) {
    return false;
  }

  // Optional fields
  if (
    data.user &&
    (typeof data.user !== "object" || typeof data.user.token !== "string")
  ) {
    return false;
  }
  if (data.policyId && typeof data.policyId !== "string") {
    return false;
  }
  if (data.policies && !Array.isArray(data.policies)) {
    return false;
  }

  return true;
}
